import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft,
  User,
  BarChart2,
  FileText,
  Bookmark,
  TreePine,
  MessageCircle,
} from "lucide-react";
import { useSupabaseAuth } from "@/context/SupabaseAuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useTheme } from "@/context/ThemeContext";
import CreditBalanceDisplay from "../credits/CreditBalanceDisplay";
import UserMenu from "@/components/auth/UserMenu";
import { useUserSubscription } from "@/utils/subscriptionUtils";
import FloatingChat from "@/components/FloatingChat";

export interface TopBarProps {
  /** Page title to display in the top bar */
  title?: string;
  /** Icon to display next to the title */
  icon?: React.ReactNode;
  /** Whether to show the back button */
  showBackButton?: boolean;
  /** Custom back navigation path */
  backPath?: string;
  /** Whether to show subscription status badge */
  showSubscriptionBadge?: boolean;
  /** Custom actions to display in the top bar */
  customActions?: React.ReactNode;
  /** Additional CSS classes for styling */
  className?: string;
  /** Whether this is the home/landing page */
  isHomePage?: boolean;
}

const TopBar: React.FC<TopBarProps> = ({
  title,
  icon,
  showBackButton = false,
  backPath,
  showSubscriptionBadge = true,
  customActions,
  className = "",
  isHomePage = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSupabaseAuth();
  const { theme } = useTheme();
  const { tier } = useUserSubscription();
  const [isChatOpen, setIsChatOpen] = useState(false);

  // Get page-specific icon if none provided
  const getPageIcon = () => {
    if (icon) return icon;

    const path = location.pathname;
    if (path === "/dashboard")
      return <BarChart2 className="h-4 w-4 text-[#40e0d0]" />;
    if (path === "/my-reports")
      return <FileText className="h-4 w-4 text-[#40e0d0]" />;
    if (path === "/my-regions")
      return <Bookmark className="h-4 w-4 text-[#40e0d0]" />;
    if (path === "/projects")
      return <TreePine className="h-4 w-4 text-[#40e0d0]" />;
    if (path === "/profile") return <User className="h-4 w-4 text-[#40e0d0]" />;

    return null;
  };

  // Get page title if none provided
  const getPageTitle = () => {
    if (title) return title;

    const path = location.pathname;
    if (path === "/dashboard") return "Dashboard";
    if (path === "/my-reports") return "My Reports";
    if (path === "/my-regions") return "Bookmarks";
    if (path === "/projects") return "Projects";
    if (path === "/profile") return "Profile Settings";

    return "";
  };

  const handleBack = () => {
    if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1);
    }
  };

  const displayTitle = getPageTitle();
  const displayIcon = getPageIcon();

  return (
    <header
      className={`sticky top-0 z-50 border-b border-border/40 backdrop-blur-xl bg-background/80 supports-[backdrop-filter]:bg-background/60 ${className}`}
    >
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex h-16 items-center justify-between">
          {/* Logo and Page Section */}
          <div className="flex items-center gap-6">
            {/* Beta and Version Badge */}
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-medium text-amber-500 bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/20">
                Beta +
              </span>
              <span className="text-[11px] text-muted-foreground">v1.3</span>
            </div>

            {/* Page Section - Only show if not home page */}
            {!isHomePage && displayTitle && (
              <>
                <div className="h-6 w-px bg-gradient-to-b from-transparent via-border to-transparent" />
                <div className="flex items-center gap-2">
                  {displayIcon && (
                    <div className="h-8 w-8 rounded-lg bg-[#40e0d0]/10 flex items-center justify-center">
                      {displayIcon}
                    </div>
                  )}
                  <h2 className="text-lg font-semibold text-foreground">
                    {displayTitle}
                  </h2>
                </div>
              </>
            )}
          </div>

          {/* Actions Section */}
          <div className="flex items-center gap-3">
            {/* User Subscription Badge - Only for authenticated users */}
            {user && showSubscriptionBadge && (
              <div
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 ${
                  tier === "PAYG"
                    ? "bg-gradient-to-r from-emerald-100 to-green-100 dark:from-emerald-900/30 dark:to-green-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-600"
                    : tier === "professional"
                      ? "bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 text-blue-700 dark:text-blue-300 border border-blue-300 dark:border-blue-600"
                      : "bg-gradient-to-r from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 text-purple-700 dark:text-purple-300 border border-purple-300 dark:border-purple-600"
                }`}
              >
                {/* Plan Icon */}
                <div
                  className={`w-2 h-2 rounded-full ${
                    tier === "PAYG"
                      ? "bg-emerald-500 animate-pulse"
                      : tier === "professional"
                        ? "bg-blue-500 animate-pulse"
                        : "bg-purple-500 animate-pulse"
                  }`}
                />

                {/* Plan Name */}
                <span className="capitalize">
                  {tier === "PAYG" ? "PAYG" : `${tier} Plan`}
                </span>

                {/* Benefits Indicator */}
                {tier !== "PAYG" && (
                  <div className="flex items-center gap-1 ml-1">
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                )}
              </div>
            )}

            {/* Upgrade Button - Only for PAYG */}
            {user && tier === "PAYG" && showSubscriptionBadge && (
              <Button
                variant="default"
                size="sm"
                className="bg-[#40e0d0]  text-xs px-4 py-2 rounded-full font-semibold hover:bg-[#09e0ce]/50 hover:text-black transition-colors duration-200 hover:border-primary/80"
                onClick={() => navigate("/professional")}
              >
                <svg
                  className="w-3 h-3 mr-1.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
                Go Professional
              </Button>
            )}

            {/* PAYG Plan Benefits */}
            {user && tier === "PAYG" && showSubscriptionBadge && (
              <div className="hidden sm:flex items-center gap-2 text-xs text-emerald-600 dark:text-emerald-400">
                <span className="md:hidden">PAYG Active</span>
              </div>
            )}

            {/* Professional Plan Benefits */}
            {user && tier === "professional" && showSubscriptionBadge && (
              <div className="hidden sm:flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
                <span className="md:hidden">Professional Active</span>
              </div>
            )}

            {/* Custom Actions */}
            {customActions}

            {/* Vertical Divider */}
            {user && <div className="h-6 w-px bg-border/60" />}

            {/* Primary Actions */}
            <div className="flex items-center gap-2">
              <div title="Toggle theme" aria-label="Toggle theme">
                <ThemeToggle />
              </div>
              {/* Chat Launcher */}
              <div className="flex items-center" title="Chat with Myǃhū AI">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setIsChatOpen(true)}
                  className="relative h-9 w-9 rounded-full border border-input bg-background hover:bg-accent hover:text-accent-foreground"
                  aria-label="Open chat"
                >
                  <MessageCircle className="h-[1.2rem] w-[1.2rem]" />
                  <span className="sr-only">Open chat</span>
                </Button>
              </div>
            </div>

            {/* Credit Balance (shown for authenticated users) */}
            {user && (
              <div className="hidden md:flex items-center ml-3">
                <CreditBalanceDisplay userId={user.id} compact={true} />
              </div>
            )}

            {/* Auth Section (UserMenu placed last for UX consistency) */}
            <div className="flex items-center gap-2">
              <UserMenu />
            </div>
            {/* Back Button */}
            {showBackButton && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="gap-2 hover:bg-[#40e0d0]/10 hover:text-[#40e0d0] transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="hidden sm:inline">Back</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* FloatingChat - controlled by TopBar */}
      {isChatOpen && (
        <FloatingChat
          groqApiKey={import.meta.env.VITE_GROQ_API_KEY}
          isOpen={isChatOpen}
          onClose={() => setIsChatOpen(false)}
        />
      )}
    </header>
  );
};

export default TopBar;
